import { DataColumnList } from './data_column';
import { DataRow } from './data_row';
import { DataType } from '../types/data_type';
import { utils } from '../utils/utils';
var EasyDataTable = /** @class */ (function () {
    function EasyDataTable(options) {
        this._chunkSize = 1000;
        this._elasticChunks = false;
        this.cachedRows = [];
        this.total = 0;
        this.loader = null;
        this.needTotal = true;
        this.isInMemory = false;
        options = options || {};
        this._chunkSize = options.chunkSize || this._chunkSize;
        this._elasticChunks = options.elasticChunks || this._elasticChunks;
        this.loader = options.loader;
        if (typeof options.inMemory !== 'undefined') {
            this.isInMemory = options.inMemory;
        }
        if (this.isInMemory) {
            this.needTotal = false;
        }
        this._columns = new DataColumnList();
        this.onUpdate = options.onUpdate;
        if (options.columns) {
            for (var _i = 0, _a = options.columns; _i < _a.length; _i++) {
                var colDesc = _a[_i];
                this._columns.add(colDesc);
            }
        }
        if (options.rows) {
            for (var _b = 0, _c = options.rows; _b < _c.length; _b++) {
                var rowData = _c[_b];
                var row = this.createRow(rowData);
                this.addRow(row);
            }
        }
        this.needTotal = !this._elasticChunks;
    }
    Object.defineProperty(EasyDataTable.prototype, "columns", {
        get: function () {
            return this._columns;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(EasyDataTable.prototype, "chunkSize", {
        get: function () {
            return this._chunkSize;
        },
        set: function (value) {
            this._chunkSize = value;
            this.total = 0;
            this.needTotal = !this.elasticChunks;
            this.cachedRows = [];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(EasyDataTable.prototype, "elasticChunks", {
        get: function () {
            return this._elasticChunks;
        },
        set: function (value) {
            this._elasticChunks = value;
            this.total = 0;
            this.needTotal = !this.elasticChunks;
            this.cachedRows = [];
        },
        enumerable: true,
        configurable: true
    });
    EasyDataTable.prototype.getRows = function (params) {
        var _this = this;
        var fromIndex = 0, count = this._chunkSize;
        if (params) {
            if ('page' in params) {
                fromIndex = params.pageSize * (params.page - 1);
                count = params.pageSize;
            }
            else {
                fromIndex = params.offset;
                count = params.limit;
            }
        }
        var endIndex = fromIndex + count; //the first index of the next page
        //if we don't calculate total on this request
        if (!this.needTotal && !this.elasticChunks) {
            if (fromIndex >= this.total) {
                return Promise.resolve([]);
            }
            if (endIndex > this.total) {
                endIndex = this.total;
            }
        }
        if (this.isInMemory && endIndex > this.cachedRows.length) {
            endIndex = this.cachedRows.length;
        }
        var allChunksCached = endIndex <= this.cachedRows.length;
        if (allChunksCached) {
            return Promise.resolve(this.cachedRows.slice(fromIndex, endIndex));
        }
        //if loader is not defined
        if (!this.loader) {
            throw "Loader is not defined. Can't get the rows from " + fromIndex + " to " + endIndex;
        }
        // we need total only for the first request
        var needTotal = this.needTotal;
        if (this.needTotal) {
            this.needTotal = false;
        }
        var offset = this.cachedRows.length;
        var limit = endIndex - offset;
        if (limit < this._chunkSize) {
            limit = this._chunkSize;
        }
        var resultPromise = this.loader.loadChunk({
            offset: offset,
            limit: limit,
            needTotal: needTotal
        })
            .then(function (result) {
            if (needTotal) {
                _this.total = result.total;
            }
            Array.prototype.push.apply(_this.cachedRows, result.table.getCachedRows());
            if (endIndex > _this.cachedRows.length) {
                endIndex = _this.cachedRows.length;
            }
            if (_this.elasticChunks) {
                var count_1 = result.table.getCachedCount();
                if (count_1 < limit) {
                    _this.total = _this.cachedRows.length;
                }
            }
            _this.fireUpdated();
            return _this.cachedRows.slice(fromIndex, endIndex);
        });
        return resultPromise;
    };
    EasyDataTable.prototype.getRow = function (index) {
        return this.getRows({ offset: index, limit: 1 })
            .then(function (rows) { return rows.length > 0 ? rows[0] : null; });
    };
    EasyDataTable.prototype.getTotal = function () {
        return this.total;
    };
    EasyDataTable.prototype.setTotal = function (total) {
        this.total = total;
        this.needTotal = false;
    };
    EasyDataTable.prototype.getCachedCount = function () {
        return this.cachedRows.length;
    };
    EasyDataTable.prototype.clear = function () {
        this.columns.clear();
        this.cachedRows = [];
        this.total = 0;
        this.needTotal = !this._elasticChunks;
        this.fireUpdated();
    };
    EasyDataTable.prototype.createRow = function (dataOrRow) {
        var _this = this;
        var dateIdx = this._columns.getDateColumnIndexes();
        var values = new Array(this._columns.count);
        var getValue = dataOrRow instanceof DataRow
            ? function (colId) { return dataOrRow.getValue(colId); }
            : function (colId) { return dataOrRow[colId]; };
        if (dataOrRow) {
            this.columns.getItems().forEach(function (column) {
                var value = getValue(column.id);
                var index = _this.columns.getIndex(column.id);
                values[index] = (dateIdx.indexOf(index) >= 0)
                    ? _this.mapDate(value, column.type)
                    : value;
            });
        }
        return new DataRow(this._columns, values);
    };
    EasyDataTable.prototype.mapDate = function (value, dtype) {
        if (value) {
            var result = new Date(value);
            if (isNaN(result.getTime())
                && dtype == DataType.Time) {
                result = utils.strToTime(value);
            }
            return result;
        }
        return null;
    };
    EasyDataTable.prototype.addRow = function (rowOrValues) {
        var newRow;
        if (Array.isArray(rowOrValues)) {
            var values = rowOrValues;
            var dateIdx = this._columns.getDateColumnIndexes();
            if (dateIdx.length > 0) {
                for (var _i = 0, dateIdx_1 = dateIdx; _i < dateIdx_1.length; _i++) {
                    var idx = dateIdx_1[_i];
                    if (values[idx]) {
                        values[idx] = this.mapDate(values[idx], this._columns.get(idx).type);
                    }
                }
            }
            newRow = new DataRow(this._columns, values);
        }
        else {
            newRow = this.createRow(rowOrValues);
        }
        this.cachedRows.push(newRow);
        var cachedTotal = this.getCachedCount();
        if (cachedTotal > this.total) {
            this.total = cachedTotal;
        }
        return newRow;
    };
    EasyDataTable.prototype.getCachedRows = function () {
        return this.cachedRows;
    };
    EasyDataTable.prototype.totalIsKnown = function () {
        if (this.elasticChunks) {
            var count = this.getCachedCount();
            return count === this.total;
        }
        return !this.needTotal;
    };
    EasyDataTable.prototype.fireUpdated = function () {
        if (this.onUpdate) {
            this.onUpdate(this);
        }
    };
    return EasyDataTable;
}());
export { EasyDataTable };
//# sourceMappingURL=easy_data_table.js.map